import React, { useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import moment from "moment";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import BalanceChart, {
  BalancePoint,
} from "features/dashboard/instrument/BalanceChart.tsx";
import Panel from "common/NewLook/Panel.tsx";
import SegmentTabs from "common/NewLook/SegmentTabs.tsx";
import { PhoneHeroRanges } from "features/dashboard/PhoneDashboard/PhoneHeroControls.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import useEarningsHistory, {
  EARNINGS_RANGES,
  EarningsRange,
} from "features/affiliation/NewLook/useEarningsHistory";
import useAffiliationTotals from "features/affiliation/NewLook/useAffiliationTotals";
import { CreatePayoutButton } from "features/affiliation/NewLook/CreatePayoutButton";
import {
  DisplayAmount,
  FigureLarge,
  InfoLabel,
  Label,
  Meta,
} from "features/affiliation/NewLook/roles";
import {
  AFFILIATION_CONTAINER,
  formatUsd,
  stripColon,
} from "features/affiliation/NewLook/newLookUtils";
import { formatDateLabel } from "@shared/utils/date";

/**
 * The overview's hero, built like the dashboard's: total earnings (the one
 * lead figure) and how they grew over the selected range, and beside it
 * where the money is now — the balance you can withdraw, what still awaits
 * confirmation and what was paid out.
 */
const OverviewHero = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const [range, setRange] = useState<EarningsRange>("all");
  const { points, total, hasEarnings, loading } = useEarningsHistory(range);
  const totals = useAffiliationTotals();

  // Hovering the chart moves the headline to that day.
  const [hovered, setHovered] = useState<BalancePoint>();
  const first = points[0]?.value ?? 0;
  const headline = hovered ? hovered.value : total;
  const change = headline - first;
  const rangeLabel = getRangeLabel(range, t("overview.range_all"));
  const compact = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        notation: "compact",
        maximumFractionDigits: 1,
      }),
    []
  );

  return (
    <Panel hero flush={true} data-testid="affiliation-overview-hero">
      <Layout>
        <Main>
          <MainHeader>
            <div>
              <Label>{t("overview.total_earnings")}</Label>
              {loading ? (
                <Skeleton width={220} height={40} style={{ marginTop: 4 }} />
              ) : (
                <DisplayAmount value={headline} currency="$" />
              )}
              {hasEarnings && (
                <ChangeLine>
                  {/* Over the whole history the change is the total. */}
                  {range !== "all" && (
                    <ChangeValue
                      style={{ color: directionColor(tokens, change) }}
                    >
                      {change > 0 ? "+" : ""}
                      {formatUsd(change)}
                    </ChangeValue>
                  )}
                  <Meta>
                    {(hovered || range === "all") && points.length
                      ? t("overview.since", {
                          time: formatDateLabel(
                            moment
                              .unix(points[0].time)
                              .add(range === "all" ? 1 : 0, "day"),
                            "MMM D, YYYY"
                          ),
                        })
                      : t("overview.over_range", { range: rangeLabel })}
                  </Meta>
                </ChangeLine>
              )}
            </div>
            {!theme.isPhone && (
              <SegmentTabs
                options={EARNINGS_RANGES}
                value={range}
                onChange={setRange}
                ariaLabel={t("overview.range_label")}
                getLabel={(option) =>
                  getRangeLabel(option, t("overview.range_all"))
                }
              />
            )}
          </MainHeader>
          <ChartArea>
            {loading ? (
              <Skeleton width="100%" height={CHART_HEIGHT} radius={8} />
            ) : hasEarnings ? (
              <BalanceChart
                points={points}
                height={CHART_HEIGHT}
                intraday={false}
                onActiveChange={setHovered}
                formatAxisValue={(value) =>
                  `$${
                    Math.abs(value) >= 1000
                      ? compact.format(value)
                      : Math.round(value).toLocaleString("en-US")
                  }`
                }
                ariaLabel={t("overview.chart_label", { range: rangeLabel })}
              />
            ) : (
              <NoHistory style={{ height: CHART_HEIGHT }}>
                {t("overview.no_earnings")}
              </NoHistory>
            )}
          </ChartArea>
          {theme.isPhone && (
            <PhoneHeroRanges<EarningsRange>
              options={EARNINGS_RANGES}
              value={range}
              onChange={setRange}
              label={t("overview.range_label")}
              surface="panel"
              getLabel={(option) =>
                getRangeLabel(option, t("overview.range_all"))
              }
            />
          )}
        </Main>

        <Side>
          <Metric>
            <Label>{stripColon(t("balance.title"))}</Label>
            <MetricRow>
              <FigureLarge>{formatUsd(totals.balance)}</FigureLarge>
              <CreatePayoutButton />
            </MetricRow>
            <Meta>{t("overview.balance_hint")}</Meta>
          </Metric>
          <Metric>
            <InfoLabel
              tooltip={t("earnings.pie_chart_tooltips.amount_not_confirmed")}
              analyticsFieldName="Awaiting confirmation"
            >
              {t("earnings.status_display_name.pending")}
            </InfoLabel>
            <FigureLarge>{formatUsd(totals.awaitingConfirmation)}</FigureLarge>
            <Meta>{t("overview.awaiting_confirmation_hint")}</Meta>
          </Metric>
          <Metric>
            <Label>{t("payouts.statistics.paid_to_date")}</Label>
            <FigureLarge>{formatUsd(totals.paidOut)}</FigureLarge>
            {totals.awaitingApproval > 0 && (
              <Meta>
                {t("overview.awaiting_approval_meta", {
                  amount: formatUsd(totals.awaitingApproval),
                })}
              </Meta>
            )}
          </Metric>
        </Side>
      </Layout>
    </Panel>
  );
};

export default OverviewHero;

/** "1M", "3M", …; "All" for the whole history. */
const getRangeLabel = (range: EarningsRange, allLabel: string) =>
  range === "all" ? allLabel : range.toUpperCase();

const CHART_HEIGHT = 184;

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  @container ${AFFILIATION_CONTAINER} (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;
const Main = styled.div`
  min-width: 0;
  padding: 20px 20px 16px 24px;
  @container ${AFFILIATION_CONTAINER} (max-width: 560px) {
    padding: 16px 16px 12px 20px;
  }
`;
const MainHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;
`;
const ChangeLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 6px;
`;
const ChangeValue = styled.span`
  ${newLookType.meta};
  font-size: 13px;
  font-weight: 500;
`;
const ChartArea = styled.div`
  margin-top: 20px;
`;
const NoHistory = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  border-radius: 8px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  text-align: center;
`;
const Side = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 24px 20px;
  border-left: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  background: ${(p) => getNewLookTokens(p.theme).well};
  border-radius: 0 12px 12px 0;
  @container ${AFFILIATION_CONTAINER} (max-width: 900px) {
    /* Under the chart the three figures read across in one row. */
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    column-gap: 24px;
    padding: 6px 24px 20px;
    border-left: none;
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    border-radius: 0 0 12px 12px;
  }
  @container ${AFFILIATION_CONTAINER} (max-width: 560px) {
    /* Phones: one row per figure. */
    grid-template-columns: minmax(0, 1fr);
    padding: 0 16px 4px 20px;
  }
`;
const Metric = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 0;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
  @container ${AFFILIATION_CONTAINER} (max-width: 900px) {
    padding: 14px 0 0;
    & + & {
      border-top: none;
    }
  }
  @container ${AFFILIATION_CONTAINER} (max-width: 560px) {
    padding: 12px 0;
    & + & {
      border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
    }
  }
`;
const MetricRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 12px;
`;
